import { Alert, AlertIcon, Button, Stack, Text } from '@chakra-ui/react';
import { CTAButton } from '@gamma/ui/buttons';
import { TextDivider } from '@gamma/ui/display';
import { ILoginForm, LoginForm } from '@gamma/ui/forms';
import { ErrorBoundary } from '@gamma/ui/overlay';
import { CardPageBody, CardPageCTA, CardPageHeading } from '@gamma/ui/pages';
import { LoadingSpinner } from '@gamma/ui/progress';
import { MFA, ROUTES } from '@investigator/shared/constants';
import { useTokenManagement } from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers';
import {
  AuthState,
  CognitoUserInterface,
} from '@investigator/shared/types/auth-types';
import { setRole } from '@investigator/shared/utils';
import { Auth } from 'aws-amplify';
import { useContext, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';

interface ILogin {
  setPreferredMFA: (e: string) => void;
}

const { home, base } = ROUTES;

export const signIn = async ({ username, password }: ILoginForm) => {
  try {
    const user: CognitoUserInterface = await Auth.signIn(username, password);
    return user;
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error('[Login] Sign in error:', error.message);
      throw new Error(error.message);
    }
    return;
  }
};

export const Login = ({ setPreferredMFA }: ILogin) => {
  const { authState, user, setAuthState, setUser, setUserRole } =
    useContext(AuthContext);

  // New token management hook
  const { saveIdToken } = useTokenManagement();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();
  const routerLocation = useLocation();

  // Check for URL error parameter
  const urlParams = new URLSearchParams(routerLocation.search);
  const urlError = urlParams.get('error');

  // Detect SSO callback - show loading instead of login form
  const isInSSOCallback =
    urlParams.has('code') && !!sessionStorage.getItem('sso_domain');

  // Show loading spinner during SSO callback processing or form submission
  if (isInSSOCallback || isLoading) {
    return <LoadingSpinner />;
  }

  const getErrorAlert = (msg: string) => {
    switch (msg) {
      case 'mfa_session_expired':
      case 'session_expired_retry': {
        const messages: Record<string, string> = {
          mfa_session_expired: i18n.pages.login.mfaErrors.sessionExpired,
          session_expired_retry: i18n.pages.login.mfaErrors.sessionExpiredRetry,
        };
        return (
          <Alert status="info" variant="solid" mt={4}>
            <AlertIcon />
            <Text>{messages[msg]}</Text>
          </Alert>
        );
      }
      case 'User is disabled.':
        return (
          <Alert status="error" variant="solid" mt={4}>
            <AlertIcon />
            <p>
              {i18n.pages.login.errors.userDisabled}&nbsp;
              <Text as="u">
                <a
                  href="https://corelight.com/support"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {i18n.pages.login.errors.supportLink}
                </a>
              </Text>
            </p>
          </Alert>
        );
      default:
        return (
          <Alert status="error" variant="solid" mt={4}>
            <AlertIcon />
            {msg}
          </Alert>
        );
    }
  };

  const handleSignIn = async (values: ILoginForm) => {
    setIsLoading(true);
    try {
      const signedInUser = await signIn(values);

      if (signedInUser) {
        if (typeof signedInUser.preferredMFA === 'string') {
          setPreferredMFA(signedInUser.preferredMFA);
        }

        switch (signedInUser.challengeName) {
          case 'NEW_PASSWORD_REQUIRED':
            navigate(
              `/registration?email=${signedInUser.username}&alias=${(signedInUser.challengeParam?.userAttributes as Record<string, string | undefined>)?.['custom:alias'] ?? ''}`,
              {
                replace: true,
                state: { user: values },
              },
            );
            break;

          case 'SOFTWARE_TOKEN_MFA':
            navigate(`mfa${routerLocation.search}`);
            setUser?.(signedInUser);
            break;
          default: {
            setAuthState?.(AuthState.SignedIn);
            setUser?.(signedInUser);
            setRole({
              user: {
                ...signedInUser,
                challengeName: signedInUser.challengeName ?? '',
                challengeParam: signedInUser.challengeParam ?? {},
              },
              setUserRole,
            });

            // Store ID token via token management hook
            const idToken = signedInUser?.signInUserSession?.idToken?.jwtToken;
            if (idToken) {
              saveIdToken(idToken, {
                ...signedInUser,
                challengeName: signedInUser.challengeName ?? '',
                challengeParam: signedInUser.challengeParam ?? {},
              });
            }

            // Go straight to root if no MFA
            if (signedInUser?.preferredMFA === MFA.NOMFA) {
              navigate(base, { replace: true });
            }
            break;
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error('[Login] Error:', err.message);
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (authState === AuthState.SignedIn && user) {
    const urlParams = new URLSearchParams(routerLocation.search);
    const next = urlParams.get('next');
    const url = next ? next : home;
    return <Navigate to={url} replace />;
  }

  const { title, troubleSigningIn, cta, ssoSignIn, orText } = i18n.pages.login;

  return (
    <ErrorBoundary page="Login page" styleClass="main">
      <CardPageHeading>
        {i18n.formatString(title, {
          appName: <strong>{i18n.app.name}</strong>,
        })}
      </CardPageHeading>
      <CardPageBody>
        <LoginForm
          onSubmit={[handleSignIn]}
          usernameType="email"
          autoFocusField="username"
          usernameContent={{
            placeholder: i18n.forms.labels.username,
            label: i18n.forms.labels.username,
          }}
          passwordContent={{
            placeholder: i18n.forms.labels.password,
            label: i18n.forms.labels.password,
          }}
          dataTestId="login"
        />
      </CardPageBody>
      {error && getErrorAlert(error)}
      {urlError && getErrorAlert(urlError)}
      <CardPageCTA>
        <Stack w="full" spacing={4}>
          <CTAButton
            data-testid="investigator-app-login-button"
            form="loginForm"
            type="submit"
            isLoading={isLoading}
          >
            {cta}
          </CTAButton>
          <TextDivider>{orText}</TextDivider>
          <CTAButton
            data-testid="investigator-oidc-login-button"
            type="button"
            onClick={() =>
              navigate(`${ROUTES.ssoLogin}${routerLocation.search}`)
            }
          >
            {ssoSignIn}
          </CTAButton>

          <Button
            variant="link"
            size="md"
            color="text.link"
            fontWeight="normal"
            onClick={() => navigate('trouble')}
          >
            {troubleSigningIn}
          </Button>
        </Stack>
      </CardPageCTA>
    </ErrorBoundary>
  );
};

export const LoginMain = Login;
